import { useMemo } from 'react';
import {
  fractionOfDay,
  formatTime,
  matchPlannedToTaps,
  plannedInstantOn,
  tapsOnDay,
  type PlannedItem,
  type TrackerAction,
  type TrackerTap,
} from '@awwyeah/shared';
import type { RosterMember } from '../useHouseholdRoster';
import { spreadLabels } from '../timelineLayout';

/** Vertical room one label row needs before the next may start (two feeds 5 min apart). */
const LABEL_GAP_PX = 22;

/** The rail starts at 5am (Dan, 2026-09-25): midnight–5am was mostly blank space on a phone.
 * Anything logged earlier pins to the top edge — still shown, never dropped. */
const RAIL_START_HOUR = 5;
const HOUR_TICKS = [5, 9, 12, 15, 18, 21, 24];

/** Fraction of the whole day (0 = midnight) → fraction of the rail (0 = 5am). */
function railFraction(dayFraction: number): number {
  const hours = dayFraction * 24;
  return Math.max(0, Math.min(1, (hours - RAIL_START_HOUR) / (24 - RAIL_START_HOUR)));
}

function tickLabel(hour: number): string {
  if (hour === 0 || hour === 24) return '12a';
  if (hour === 12) return '12p';
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}

/**
 * The day view's whole point (2026-09-25): plan on the left, what actually happened on the
 * right, both plotted against one shared vertical clock so "logged near its planned time"
 * is something the eye does, not something a label has to claim.
 *
 * Deliberately not a chart of lateness. A planned item with no nearby log just shows the
 * plan and nothing else next to it — same neutral dot it would have gotten if it matched,
 * minus the log. See the module contract in `@awwyeah/shared`'s trackers docblock: nothing
 * here may read as overdue, and this view is the one place a "due" reading would be easiest
 * to back into, so the styling is the same regardless of whether a match exists.
 */
export function DayTimeline({
  actions,
  planned,
  taps,
  day,
  now,
  isToday,
  getMember,
}: {
  actions: readonly TrackerAction[];
  planned: readonly PlannedItem[];
  taps: readonly TrackerTap[];
  day: Date;
  now: Date;
  isToday: boolean;
  getMember: (id: string) => RosterMember;
}): JSX.Element {
  const actionById = useMemo(() => new Map(actions.map((a) => [a.id, a])), [actions]);
  const matches = useMemo(() => matchPlannedToTaps(planned, taps, day), [planned, taps, day]);
  const matchedTapIds = useMemo(
    () => new Set(matches.flatMap((m) => (m.tap ? [m.tap.id] : []))),
    [matches],
  );
  // Logs a plan didn't claim still get their own mark on the "actual" side — the timeline
  // never hides a real event just because nothing was scheduled for it.
  const logs = useMemo(
    () => tapsOnDay(taps, day).filter((tap) => actionById.has(tap.actionId)),
    [taps, day, actionById],
  );
  const nowFraction = railFraction(fractionOfDay(now.toISOString(), day));

  const RAIL_HEIGHT = 640;
  // Ideal spot is the time on the rail; labels that would overprint slide just clear.
  const planYs = spreadLabels(
    matches.map(({ item }) => railFraction(plannedFraction(item.time)) * RAIL_HEIGHT),
    LABEL_GAP_PX,
    RAIL_HEIGHT,
  );
  const logYs = spreadLabels(
    logs.map((tap) => railFraction(fractionOfDay(tap.at, day)) * RAIL_HEIGHT),
    LABEL_GAP_PX,
    RAIL_HEIGHT,
  );

  return (
    <div className="flex flex-col gap-2" data-testid="tracker-day-timeline">
      <div className="grid grid-cols-[1fr_auto_1fr] gap-3">
        <div className="pt-1 text-right text-xs font-semibold uppercase tracking-wide text-fg-3">
          Planned
        </div>
        <div />
        <div className="pt-1 text-xs font-semibold uppercase tracking-wide text-fg-3">Actual</div>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] gap-3">
        {/* Planned column */}
        <div className="relative" style={{ height: RAIL_HEIGHT }}>
          {matches.map(({ item, tap }, index) => {
            const y = planYs[index] ?? 0;
            return (
              <div
                key={item.id}
                data-testid="tracker-plan-mark"
                data-done={tap ? 'true' : 'false'}
                className="absolute right-0 flex max-w-full items-center justify-end gap-1.5 text-right"
                style={{ top: y, transform: 'translateY(-50%)' }}
              >
                <span className="truncate text-[13px] text-fg-2">
                  <span className="font-semibold text-fg">{item.label}</span> ·{' '}
                  {formatTime(plannedInstantOn(item, day).toISOString())}
                </span>
                <span
                  aria-hidden="true"
                  className={`h-2.5 w-2.5 flex-shrink-0 rounded-full border-2 ${
                    tap ? 'border-fg-2 bg-fg-2' : 'border-line-strong bg-surface'
                  }`}
                />
              </div>
            );
          })}
          {planned.length === 0 && (
            <p className="text-sm text-fg-3">No plan set for this tracker yet.</p>
          )}
        </div>

        {/* Shared rail with hour ticks */}
        <div className="relative w-px bg-line" style={{ height: RAIL_HEIGHT }} aria-hidden="true">
          {HOUR_TICKS.map((hour) => (
            <span
              key={hour}
              className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium text-fg-3"
              style={{ top: railFraction(hour / 24) * RAIL_HEIGHT - 6 }}
            >
              {tickLabel(hour)}
            </span>
          ))}
          {isToday && (
            <span
              data-testid="tracker-day-now"
              className="absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-fg"
              style={{ top: nowFraction * RAIL_HEIGHT }}
            />
          )}
        </div>

        {/* Actual column */}
        <div className="relative" style={{ height: RAIL_HEIGHT }}>
          {logs.map((tap, index) => {
            const action = actionById.get(tap.actionId);
            const member = getMember(tap.byId);
            const y = logYs[index] ?? 0;
            return (
              <div
                key={tap.id}
                data-testid="tracker-log-mark"
                data-matched={matchedTapIds.has(tap.id) ? 'true' : 'false'}
                className="absolute left-0 flex max-w-full items-center gap-1.5"
                style={{ top: y, transform: 'translateY(-50%)' }}
              >
                <span
                  aria-hidden="true"
                  className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] leading-none"
                  style={{ backgroundColor: member.color }}
                >
                  {action?.emoji ?? ''}
                </span>
                <span className="truncate text-[13px] text-fg-2">
                  <span className="font-semibold text-fg">{action?.label ?? 'Done'}</span> ·{' '}
                  {formatTime(tap.at)} ·{' '}
                  <span style={{ color: member.color }} className="font-semibold">
                    {member.name.trim().split(/\s+/)[0]}
                  </span>
                </span>
              </div>
            );
          })}
          {logs.length === 0 && (
            <p className="text-sm text-fg-3">
              {isToday ? 'Nothing logged yet.' : 'Nothing logged.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** `"07:30"` → `0.3125` — the planned column's own vertical placement, independent of `Date`. */
function plannedFraction(time: string): number {
  const [hourStr, minuteStr] = time.split(':');
  const hour = Number(hourStr ?? 0);
  const minute = Number(minuteStr ?? 0);
  return Math.max(0, Math.min(1, (hour * 60 + minute) / (24 * 60)));
}
